<template>
  <div class="pg-pip">
    <!-- 헤더 -->
    <div class="pg-pip__head">
      <h6 class="pg-pip__title">
        <i class="material-icons-round">forum</i>
        PIP 회화 연습
      </h6>
      <p class="pg-pip__subtitle">Pronunciation, Interpretation, Performance</p>
    </div>

    <!-- 언어 설정 카드 -->
    <section class="pg-pip__card">
      <div class="pg-pip__card-head">
        <i class="material-icons-round">language</i>
        <span>언어 설정</span>
      </div>
      <div class="pg-pip__card-body">
        <div class="pg-pip__lang-row">
          <div class="pg-pip__lang-item">
            <div class="pg-pip__lang-icon pg-pip__lang-icon--source">
              <i class="material-icons-round">mic</i>
            </div>
            <div class="pg-pip__lang-field p-field">
              <label class="p-field__label">소스 언어</label>
              <select v-model="sectionData.content.sourceLanguageCode" class="p-input">
                <option value="ko">🇰🇷 한국어</option>
                <option value="en">🇺🇸 영어</option>
                <option value="ja">🇯🇵 일본어</option>
                <option value="zh">🇨🇳 중국어</option>
                <option value="es">🇪🇸 스페인어</option>
                <option value="fr">🇫🇷 프랑스어</option>
              </select>
            </div>
          </div>

          <div class="pg-pip__lang-arrow">
            <i class="material-icons-round">arrow_forward</i>
          </div>

          <div class="pg-pip__lang-item">
            <div class="pg-pip__lang-icon pg-pip__lang-icon--target">
              <i class="material-icons-round">my_location</i>
            </div>
            <div class="pg-pip__lang-field p-field">
              <label class="p-field__label">타겟 언어</label>
              <select v-model="sectionData.content.targetLanguageCode" class="p-input">
                <option value="ko">🇰🇷 한국어</option>
                <option value="en">🇺🇸 영어</option>
                <option value="ja">🇯🇵 일본어</option>
                <option value="zh">🇨🇳 중국어</option>
                <option value="es">🇪🇸 스페인어</option>
                <option value="fr">🇫🇷 프랑스어</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 화자 설정 카드 -->
    <section class="pg-pip__card">
      <div class="pg-pip__card-head">
        <i class="material-icons-round">groups</i>
        <span>화자 설정</span>
      </div>
      <div class="pg-pip__card-body">
        <div class="pg-pip__speakers">
          <!-- 화자 A -->
          <div class="pg-pip__speaker">
            <div class="pg-pip__speaker-head">
              <h6 class="pg-pip__speaker-title">
                <i class="material-icons-round">person</i>
                화자 A
              </h6>
            </div>
            <div class="pg-pip__speaker-body">
              <div class="p-form-grid p-form-grid--2">
                <div class="p-field">
                  <label class="p-field__label">화자 이름 (한국어)</label>
                  <input
                    v-model="sectionData.content.speakerA.name"
                    @blur="handleSpeakerNameBlur('speakerA', 'name')"
                    class="p-input"
                    placeholder="예: 김민준"
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">화자 이름 (영문)</label>
                  <input
                    v-model="sectionData.content.speakerA.nameEn"
                    @blur="handleSpeakerNameBlur('speakerA', 'nameEn')"
                    class="p-input"
                    placeholder="예: Kim Minjoon"
                  />
                </div>
              </div>

              <!-- 화자 A 아이콘 -->
              <div class="pg-pip__upload">
                <label class="p-field__label">화자 아이콘</label>
                <div class="pg-pip__upload-area">
                  <input
                    type="file"
                    @change="handleSpeakerIcon($event, 'speakerA')"
                    accept="image/*"
                    class="pg-pip__file-input"
                    id="speaker-a-icon"
                  />
                  <label for="speaker-a-icon" class="pg-pip__upload-label">
                    <div v-if="uploadingState.speakerA" class="pg-pip__upload-loading">
                      <div class="pg-pip__progress-wrap">
                        <div class="pg-pip__progress-circle">
                          <i class="material-icons-round">sync</i>
                        </div>
                        <div class="pg-pip__progress-bar">
                          <div class="pg-pip__progress-fill" :style="{ width: uploadProgress.speakerA + '%' }"></div>
                        </div>
                        <span class="pg-pip__progress-text">{{ uploadProgress.speakerA }}%</span>
                      </div>
                    </div>
                    <div v-else-if="sectionData.content.speakerA.icon" class="pg-pip__avatar">
                      <img :src="sectionData.content.speakerA.icon" alt="Speaker A Icon" />
                    </div>
                    <div v-else class="pg-pip__avatar-empty">
                      <i class="material-icons-round">photo_camera</i>
                      <span>이미지 선택</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- 화자 B -->
          <div class="pg-pip__speaker">
            <div class="pg-pip__speaker-head">
              <h6 class="pg-pip__speaker-title">
                <i class="material-icons-round">person</i>
                화자 B
              </h6>
            </div>
            <div class="pg-pip__speaker-body">
              <div class="p-form-grid p-form-grid--2">
                <div class="p-field">
                  <label class="p-field__label">화자 이름 (한국어)</label>
                  <input
                    v-model="sectionData.content.speakerB.name"
                    @blur="handleSpeakerNameBlur('speakerB', 'name')"
                    class="p-input"
                    placeholder="예: 이지현"
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">화자 이름 (영문)</label>
                  <input
                    v-model="sectionData.content.speakerB.nameEn"
                    @blur="handleSpeakerNameBlur('speakerB', 'nameEn')"
                    class="p-input"
                    placeholder="예: Lee Jihyun"
                  />
                </div>
              </div>

              <!-- 화자 B 아이콘 -->
              <div class="pg-pip__upload">
                <label class="p-field__label">화자 아이콘</label>
                <div class="pg-pip__upload-area">
                  <input
                    type="file"
                    @change="handleSpeakerIcon($event, 'speakerB')"
                    accept="image/*"
                    class="pg-pip__file-input"
                    id="speaker-b-icon"
                  />
                  <label for="speaker-b-icon" class="pg-pip__upload-label">
                    <div v-if="uploadingState.speakerB" class="pg-pip__upload-loading">
                      <div class="pg-pip__progress-wrap">
                        <div class="pg-pip__progress-circle">
                          <i class="material-icons-round">sync</i>
                        </div>
                        <div class="pg-pip__progress-bar">
                          <div class="pg-pip__progress-fill" :style="{ width: uploadProgress.speakerB + '%' }"></div>
                        </div>
                        <span class="pg-pip__progress-text">{{ uploadProgress.speakerB }}%</span>
                      </div>
                    </div>
                    <div v-else-if="sectionData.content.speakerB.icon" class="pg-pip__avatar">
                      <img :src="sectionData.content.speakerB.icon" alt="Speaker B Icon" />
                    </div>
                    <div v-else class="pg-pip__avatar-empty">
                      <i class="material-icons-round">photo_camera</i>
                      <span>이미지 선택</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PIP 스크립트 카드 -->
    <section class="pg-pip__card">
      <div class="pg-pip__card-head">
        <i class="material-icons-round">description</i>
        <span>PIP 스크립트</span>
      </div>
      <div class="pg-pip__card-body">
        <AEmpty
          v-if="sectionData.content.pipScripts.length === 0"
          title="스크립트가 없습니다"
          sub="새로운 대화 스크립트를 추가해주세요"
        />

        <div class="pg-pip__scripts">
          <div
            v-for="(script, index) in sectionData.content.pipScripts"
            :key="index"
            class="pg-pip__item"
          >
            <div class="pg-pip__item-head">
              <div class="pg-pip__item-info">
                <span class="pg-pip__index">{{ index + 1 }}</span>
                <div class="pg-pip__item-title">
                  <h6>스크립트 {{ index + 1 }}</h6>
                  <APill tone="ink">턴 {{ script.turn }}</APill>
                </div>
              </div>
              <div class="pg-pip__item-actions">
                <button
                  type="button"
                  class="pg-pip__icon-btn pg-pip__icon-btn--danger"
                  title="스크립트 삭제"
                  @click.prevent="removePipScript(index)"
                >
                  <i class="material-icons-round">delete</i>
                </button>
              </div>
            </div>

            <!-- 화자 선택 섹션 -->
            <div class="pg-pip__section">
              <h6 class="pg-pip__section-title">
                <i class="material-icons-round">account_circle</i>
                화자 선택
              </h6>
              <div class="p-form-grid p-form-grid--2 pg-pip__section-grid">
                <div class="p-field">
                  <label class="p-field__label">화자</label>
                  <select v-model="script.selectedSpeaker" class="p-input">
                    <option value="">화자를 선택하세요</option>
                    <option value="speakerA">
                      화자 A - {{ getSpeakerDisplayName('speakerA') }}
                    </option>
                    <option value="speakerB">
                      화자 B - {{ getSpeakerDisplayName('speakerB') }}
                    </option>
                  </select>
                </div>
                <div class="p-field">
                  <label class="p-field__label">턴 순서</label>
                  <input
                    v-model.number="script.turn"
                    type="number"
                    class="p-input"
                    placeholder="순서"
                    min="1"
                  />
                </div>
              </div>

              <!-- 선택된 화자 정보 표시 -->
              <div v-if="script.selectedSpeaker" class="pg-pip__speaker-info">
                <div class="pg-pip__speaker-display">
                  <div class="pg-pip__speaker-avatar">
                    <div v-if="getSelectedSpeakerIcon(script.selectedSpeaker)" class="pg-pip__avatar pg-pip__avatar--sm">
                      <img :src="getSelectedSpeakerIcon(script.selectedSpeaker)" :alt="`${script.selectedSpeaker} Icon`" />
                    </div>
                    <div v-else class="pg-pip__avatar-empty pg-pip__avatar-empty--sm">
                      <i class="material-icons-round">person</i>
                    </div>
                  </div>
                  <div class="pg-pip__speaker-meta">
                    <div class="pg-pip__speaker-name">{{ getSelectedSpeakerName(script.selectedSpeaker) }}</div>
                    <div class="pg-pip__speaker-name-en">{{ getSelectedSpeakerNameEn(script.selectedSpeaker) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 대화 내용 섹션 -->
            <div class="pg-pip__section">
              <h6 class="pg-pip__section-title">
                <i class="material-icons-round">forum</i>
                대화 내용
              </h6>
              <div class="pg-pip__text-pair">
                <div class="pg-pip__text-item pg-pip__text-item--source p-field">
                  <label class="p-field__label">
                    <i class="material-icons-round">mic</i>
                    소스 텍스트
                  </label>
                  <textarea
                    v-model="script.sourceText"
                    class="p-input p-input--area"
                    rows="4"
                    placeholder="원문을 입력하세요..."
                    maxlength="200"
                  />
                  <div class="pg-pip__char-count">{{ script.sourceText?.length || 0 }}/200</div>
                </div>
                <div class="pg-pip__text-item pg-pip__text-item--target p-field">
                  <label class="p-field__label">
                    <i class="material-icons-round">my_location</i>
                    타겟 텍스트
                  </label>
                  <textarea
                    v-model="script.targetText"
                    class="p-input p-input--area"
                    rows="4"
                    placeholder="번역문을 입력하세요..."
                    maxlength="200"
                  />
                  <div class="pg-pip__char-count">{{ script.targetText?.length || 0 }}/200</div>
                </div>
              </div>
            </div>

            <!-- 발음 및 오디오 섹션 -->
            <div class="pg-pip__section">
              <h6 class="pg-pip__section-title">
                <i class="material-icons-round">volume_up</i>
                발음 및 오디오
              </h6>
              <div class="p-form-grid p-form-grid--2">
                <div class="p-field">
                  <label class="p-field__label">타겟 언어 정답 발음</label>
                  <input
                    v-model="script.targetTextPronunciation"
                    class="p-input"
                    placeholder="정확한 발음을 입력하세요. 정답은 여러개 입력 가능 쉼표(,)로 구분"
                    maxlength="400"
                  />
                </div>
                <div class="p-field">
                  <label class="p-field__label">오디오 파일</label>
                  <div class="pg-pip__upload-area">
                    <input
                      type="file"
                      @change="handleAudioFile($event, index)"
                      accept="audio/*"
                      class="pg-pip__file-input"
                      :id="`audio-file-${index}`"
                    />
                    <label :for="`audio-file-${index}`" class="pg-pip__upload-label pg-pip__upload-label--audio">
                      <div v-if="uploadingState[`audio_${index}`]" class="pg-pip__upload-loading pg-pip__upload-loading--audio">
                        <div class="pg-pip__progress-wrap">
                          <div class="pg-pip__progress-circle">
                            <i class="material-icons-round">sync</i>
                          </div>
                          <div class="pg-pip__progress-bar">
                            <div class="pg-pip__progress-fill" :style="{ width: uploadProgress[`audio_${index}`] + '%' }"></div>
                          </div>
                          <span class="pg-pip__progress-text">{{ uploadProgress[`audio_${index}`] }}%</span>
                        </div>
                      </div>
                      <div v-else-if="script.fileName" class="pg-pip__audio-preview">
                        <div class="pg-pip__audio-controls">
                          <button
                            type="button"
                            class="pg-pip__play-btn"
                            @click="playAudio(script.fileName, index)"
                            :title="isPlaying[index] ? '일시정지' : '재생'"
                          >
                            <i class="material-icons-round">{{ isPlaying[index] ? 'pause' : 'play_arrow' }}</i>
                          </button>
                          <div class="pg-pip__audio-info">
                            <i class="material-icons-round">music_note</i>
                            <span class="pg-pip__audio-name">{{ script.fileName }}</span>
                          </div>
                          <button
                            type="button"
                            class="pg-pip__change-btn"
                            @click="changeAudioFile(index)"
                            title="파일 변경"
                          >
                            <i class="material-icons-round">edit</i>
                          </button>
                        </div>
                      </div>
                      <div v-else class="pg-pip__audio-empty">
                        <div class="pg-pip__audio-empty-inner">
                          <i class="material-icons-round">mic</i>
                          <span>오디오 파일 선택</span>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- 화행(Speech Act) 섹션 -->
            <div class="pg-pip__section">
              <h6 class="pg-pip__section-title">
                <i class="material-icons-round">chat_bubble</i>
                화행(Speech Act)
              </h6>
              <div class="p-field">
                <label class="p-field__label">화행 설명</label>
                <textarea
                  v-model="script.performanceDescription"
                  class="p-input p-input--area"
                  rows="3"
                  placeholder="이 대화의 화행(Speech Act)을 설명하세요. 예: 인사, 요청, 제안, 거절 등..."
                  maxlength="500"
                />
                <div class="pg-pip__char-count pg-pip__char-count--inline">{{ script.performanceDescription?.length || 0 }}/500</div>
              </div>
            </div>

            <!-- 시간 설정 섹션 -->
            <div class="pg-pip__section">
              <h6 class="pg-pip__section-title">
                <i class="material-icons-round">schedule</i>
                시간 설정
              </h6>
              <div class="pg-pip__time-row">
                <div class="pg-pip__time-item">
                  <div class="pg-pip__time-icon pg-pip__time-icon--thinking">
                    <i class="material-icons-round">psychology</i>
                  </div>
                  <div class="pg-pip__time-content p-field">
                    <label class="p-field__label">생각하는 시간 (단위: 초)</label>
                    <input
                      type="number"
                      v-model.number="script.thinkingTime"
                      class="p-input"
                      min="0"
                      placeholder="5"
                    />
                  </div>
                </div>
                <div class="pg-pip__time-item">
                  <div class="pg-pip__time-icon pg-pip__time-icon--speaking">
                    <i class="material-icons-round">record_voice_over</i>
                  </div>
                  <div class="pg-pip__time-content p-field">
                    <label class="p-field__label">말하기 시간 (단위: 초)</label>
                    <input
                      type="number"
                      v-model.number="script.speakingTime"
                      class="p-input"
                      min="1"
                      placeholder="10"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 스크립트 추가 버튼 -->
        <div class="pg-pip__actions">
          <ABtn kind="yellow" icon="add" @click.prevent="addPipScript">스크립트 추가</ABtn>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { defineModel, watch, reactive, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import ABtn from '@/components/Pineapple/ABtn.vue';
import APill from '@/components/Pineapple/APill.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';

const route = useRoute();
const instance = getCurrentInstance();
const $axios = instance?.proxy?.$axios;

const sectionData = defineModel({ type: Object, required: true });

// 부모(PopupLayerSection)에서 전달하는 챕터 번호
const props = defineProps({
  chapterNumber: { type: [Number, String], default: null },
});

// 업로드 상태 관리
const uploadingState = reactive({
  speakerA: false,
  speakerB: false
});

// 업로드 진행률 관리
const uploadProgress = reactive({
  speakerA: 0,
  speakerB: 0
});

// 오디오 재생 상태 관리
const isPlaying = reactive({});
const currentAudio = reactive({});



// 데이터 구조 초기화
watch(() => sectionData.value.content, (newContent) => {
  if (!newContent.sourceLanguageCode) {
    sectionData.value.content.sourceLanguageCode = 'ko';
  }
  if (!newContent.targetLanguageCode) {
    sectionData.value.content.targetLanguageCode = 'en';
  }
  if (!newContent.speakerA) {
    sectionData.value.content.speakerA = {
      name: '',
      nameEn: '',
      icon: null
    };
  }
  if (!newContent.speakerB) {
    sectionData.value.content.speakerB = {
      name: '',
      nameEn: '',
      icon: null
    };
  }
  if (!newContent.pipScripts) {
    sectionData.value.content.pipScripts = [];
  }
}, { immediate: true, deep: true });

// PIP 스크립트 추가
const addPipScript = () => {
  const newTurn = sectionData.value.content.pipScripts.length + 1;
  sectionData.value.content.pipScripts.push({
    selectedSpeaker: '',
    sourceText: '',
    targetText: '',
    targetTextPronunciation: '',
    performanceDescription: '',
    fileName: '',
    turn: newTurn,
    thinkingTime: 5,
    speakingTime: 10
  });
};

// PIP 스크립트 삭제
const removePipScript = (index) => {
  sectionData.value.content.pipScripts.splice(index, 1);
  // 턴 순서 재정렬
  sectionData.value.content.pipScripts.forEach((script, idx) => {
    script.turn = idx + 1;
  });
};

// 화자 이름 정규화: 앞뒤 공백 제거 + 이름 중간 연속 공백은 하나로
// 화자 영문명은 녹음 파일 경로의 일부로 쓰여 공백 하나에 채점·재생이 실패한다
const normalizeSpeakerName = (value) => {
  if (typeof value !== "string") return value;
  return value.trim().replace(/\s+/g, " ");
};

// 입력란에서 포커스가 빠질 때 화자 이름을 정규화한다
const handleSpeakerNameBlur = (speakerType, field) => {
  const speaker = sectionData.value.content?.[speakerType];
  if (!speaker) return;
  speaker[field] = normalizeSpeakerName(speaker[field] || "");
};

// 화자 아이콘 처리 (기본 설정의 화자 A, B용)
const handleSpeakerIcon = async (event, speakerType) => {
  const file = event.target.files[0];
  if (file) {
    // 이미지 크기 제한 (예: 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('이미지 크기는 2MB 이하여야 합니다.');
      return;
    }

    // 이미지 파일 형식 확인
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }

    // 확장자는 png, jpg, jpeg 만 가능
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      alert('확장자는 png, jpg, jpeg 만 가능합니다.');
      return;
    }

    // 업로드 시작 상태로 변경
    uploadingState[speakerType] = true;
    uploadProgress[speakerType] = 0;

    try {
      // 파일 업로드 API 사용
      const uploadedUrl = await uploadSpeakerIcon(file, speakerType);
      sectionData.value.content[speakerType].icon = uploadedUrl;
      console.log(`화자 ${speakerType} 아이콘 업로드 성공:`, uploadedUrl);

    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert(`이미지 업로드에 실패했습니다: ${error.message}`);
    } finally {
      // 업로드 완료 후 상태 초기화
      uploadingState[speakerType] = false;
      uploadProgress[speakerType] = 0;

      // 파일 input 초기화
      event.target.value = '';
    }
  }
};

// 파일 업로드 API 함수 (ImageStorage.vue의 uploadSingleFile 참고)
const uploadSpeakerIcon = async (file, speakerType) => {
  const barcode = route.query.barcode;

  if (!barcode) {
    throw new Error('barcode가 없습니다. props 또는 route.query.barcode를 확인해주세요.');
  }

  // 파일명을 화자이름으로 변경
  const fileName = `${normalizeSpeakerName(sectionData.value.content[speakerType]?.nameEn || "")}.png`;
  const renamedFile = new File([file], fileName, { type: file.type });

  const formData = new FormData();
  formData.append('files', renamedFile); // 서버 API 스펙에 맞춰 'files'로 설정
  formData.append('uploadPath', `pip/${barcode}`);

  const res = await $axios({
    method: "post",
    url: '/common/fileUpload',
    data: formData,
    loadingBar: true,
    onUploadProgress: (progressEvent) => {
      uploadProgress[speakerType] = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
    }
  });

  if (res.data.success && res.data.uploaded_files?.length > 0) {
    return res.data.uploaded_files[0];
  } else {
    throw new Error('업로드된 파일 정보가 없습니다.');
  }
};

// 오디오 파일 처리 함수
const handleAudioFile = async (event, scriptIndex) => {
  const file = event.target.files[0];
  if (file) {
    // 오디오 파일 크기 제한 (예: 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('오디오 파일 크기는 10MB 이하여야 합니다.');
      return;
    }

    // 오디오 파일 형식 확인
    if (!file.type.startsWith('audio/')) {
      alert('오디오 파일만 업로드 가능합니다.');
      return;
    }

    const audioKey = `audio_${scriptIndex}`;

    // 업로드 시작 상태로 변경
    uploadingState[audioKey] = true;
    uploadProgress[audioKey] = 0;

    try {
      // 파일 업로드 API 사용 (아이콘과 같은 경로)
      const uploadedUrl = await uploadAudioFile(file, scriptIndex);
      sectionData.value.content.pipScripts[scriptIndex].fileName = uploadedUrl;
      console.log(`스크립트 ${scriptIndex + 1} 오디오 파일 업로드 성공:`, uploadedUrl);

    } catch (error) {
      console.error('오디오 파일 업로드 실패:', error);
      alert(`오디오 파일 업로드에 실패했습니다: ${error.message}`);
    } finally {
      // 업로드 완료 후 상태 초기화
      uploadingState[audioKey] = false;
      uploadProgress[audioKey] = 0;

      // 파일 input 초기화
      event.target.value = '';
    }
  }
};

// 오디오 파일 업로드 API 함수
const uploadAudioFile = async (file, scriptIndex) => {
  const barcode = route.query.barcode;

  if (!barcode) {
    throw new Error('barcode가 없습니다. props 또는 route.query.barcode를 확인해주세요.');
  }

  // 현재 스크립트 정보 가져오기
  const currentScript = sectionData.value.content.pipScripts[scriptIndex];

  // 선택된 화자의 영문명 가져오기
  const selectedSpeaker = currentScript.selectedSpeaker; // 'speakerA' 또는 'speakerB'
  const speakerNameEn = normalizeSpeakerName(sectionData.value.content[selectedSpeaker]?.nameEn || '') || 'Unknown';

  // chapter 번호: 부모에서 전달한 값 우선, 없으면 section 데이터에서 fallback
  const chapterNumber = props.chapterNumber;

  // 턴 번호는 scriptIndex + 1 (1부터 시작)
  const turnNumber = currentScript.turn || (scriptIndex + 1);

  // 오디오 파일명이 {barcode}_{chapterNumber}_{화자영문명}_{turn 번호}.mp3
  const fileName = `${barcode}_${chapterNumber}_${speakerNameEn}_${turnNumber}.mp3`;
  const renamedFile = new File([file], fileName, { type: file.type });

  const formData = new FormData();
  formData.append('files', renamedFile);
  formData.append('uploadPath', `pip/${barcode}`); // 아이콘과 같은 경로

  const audioKey = `audio_${scriptIndex}`;

  const res = await $axios({
    method: "post",
    url: '/common/fileUpload',
    data: formData,
    loadingBar: true,
    onUploadProgress: (progressEvent) => {
      uploadProgress[audioKey] = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      );
    }
  });

  if (res.data.success && res.data.uploaded_files?.length > 0) {
    return res.data.uploaded_files[0];
  } else {
    throw new Error('업로드된 파일 정보가 없습니다.');
  }
};

// 오디오 재생 함수 (캐시 회피를 위해 URL에 타임스탬프 추가)
const playAudio = (audioUrl, scriptIndex) => {
  if (!audioUrl) return;

  // 이전에 재생 중인 오디오가 있으면 정지
  if (currentAudio[scriptIndex]) {
    currentAudio[scriptIndex].pause();
    currentAudio[scriptIndex] = null;
    isPlaying[scriptIndex] = false;
  }

  // 캐시 방지: 동일 URL이라도 재업로드 후 최신 파일 재생
  const separator = audioUrl.includes('?') ? '&' : '?';
  const urlWithCacheBust = `${audioUrl}${separator}_t=${Date.now()}`;

  // 새로운 오디오 생성 및 재생
  const audio = new Audio(urlWithCacheBust);
  currentAudio[scriptIndex] = audio;

  audio.addEventListener('ended', () => {
    isPlaying[scriptIndex] = false;
  });

  audio.addEventListener('error', (error) => {
    console.error('오디오 재생 오류:', error);
    alert('오디오 파일을 재생할 수 없습니다.');
    isPlaying[scriptIndex] = false;
  });

  audio.play().then(() => {
    isPlaying[scriptIndex] = true;
  }).catch((error) => {
    console.error('오디오 재생 실패:', error);
    alert('오디오 파일을 재생할 수 없습니다.');
    isPlaying[scriptIndex] = false;
  });
};

// 오디오 파일 변경 함수
const changeAudioFile = (scriptIndex) => {
  // 현재 재생 중인 오디오 정지
  if (currentAudio[scriptIndex]) {
    currentAudio[scriptIndex].pause();
    currentAudio[scriptIndex] = null;
    isPlaying[scriptIndex] = false;
  }

  // 파일 input 클릭
  const fileInput = document.getElementById(`audio-file-${scriptIndex}`);
  if (fileInput) {
    fileInput.click();
  }
};

// 화자 표시 이름 가져오기
const getSpeakerDisplayName = (speakerType) => {
  const speaker = sectionData.value.content[speakerType];
  return speaker?.name || '이름 없음';
};

// 선택된 화자의 아이콘 가져오기
const getSelectedSpeakerIcon = (speakerType) => {
  return sectionData.value.content[speakerType]?.icon || null;
};

// 선택된 화자의 이름 가져오기
const getSelectedSpeakerName = (speakerType) => {
  return sectionData.value.content[speakerType]?.name || '';
};

// 선택된 화자의 영문 이름 가져오기
const getSelectedSpeakerNameEn = (speakerType) => {
  return sectionData.value.content[speakerType]?.nameEn || '';
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-pip {
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* ======================
     헤더
     ====================== */
  &__head {
    text-align: center;
    padding-bottom: 12px;
    border-bottom: 1px solid $p-line;
  }

  &__title {
    margin: 0;
    font-size: 18px;
    font-weight: 800;
    color: $p-ink;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    letter-spacing: -0.015em;

    > i {
      color: $p-point-deep;
      font-size: 22px;
    }
  }

  &__subtitle {
    margin: 6px 0 0;
    font-size: 12px;
    color: $p-ink-mid;
    font-weight: 500;
  }

  /* ======================
     섹션 카드
     ====================== */
  &__card {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    overflow: hidden;
    transition: border-color $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
    }
  }

  &__card-head {
    padding: 12px 16px;
    background: $p-panel-alt;
    border-bottom: 1px solid $p-line;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    letter-spacing: -0.01em;

    > i {
      color: $p-ink-mid;
      font-size: 18px;
    }
  }

  &__card-body {
    padding: 16px;
  }

  /* ======================
     언어 선택기
     ====================== */
  &__lang-row {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 12px;
    align-items: center;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__lang-item {
    background: $p-panel-alt;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__lang-icon {
    width: 36px;
    height: 36px;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > i {
      font-size: 18px;
    }

    &--source {
      background: $p-ink;
      color: $p-point;
    }

    &--target {
      background: $p-point;
      color: $p-ink;
    }
  }

  &__lang-field {
    flex: 1;
    min-width: 0;
  }

  &__lang-arrow {
    display: grid;
    place-items: center;
    color: $p-ink-mid;
    font-size: 20px;

    @media (max-width: 768px) {
      transform: rotate(90deg);
    }
  }

  /* ======================
     화자 카드
     ====================== */
  &__speakers {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__speaker {
    background: $p-panel-alt;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    padding: 14px;
    transition: border-color $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
    }
  }

  &__speaker-head {
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px dashed $p-line;
  }

  &__speaker-title {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    display: flex;
    align-items: center;
    gap: 6px;

    > i {
      color: $p-ink-mid;
      font-size: 16px;
    }
  }

  &__speaker-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  /* ======================
     스크립트 카드
     ====================== */
  &__scripts {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 16px;
    transition: border-color $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
    }
  }

  &__item-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
  }

  &__item-info {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__index {
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__item-title {
    display: flex;
    flex-direction: column;
    gap: 4px;

    > h6 {
      margin: 0;
      font-size: 14px;
      font-weight: 700;
      color: $p-ink;
    }
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &--danger {
      color: $p-err;

      &:hover:not(:disabled) {
        background: $p-err-soft;
        color: $p-err;
      }
    }
  }

  /* ======================
     스크립트 섹션 (script-section)
     ====================== */
  &__section {
    background: $p-panel-alt;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    padding: 12px 14px;
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__section-title {
    margin: 0 0 12px;
    padding-bottom: 8px;
    border-bottom: 1px dashed $p-line;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
    display: flex;
    align-items: center;
    gap: 6px;
    letter-spacing: -0.01em;

    > i {
      color: $p-ink-mid;
      font-size: 14px;
    }
  }

  &__section-grid {
    grid-template-columns: 2fr 1fr;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  /* ======================
     선택된 화자 정보
     ====================== */
  &__speaker-info {
    margin-top: 12px;
    padding: 10px;
    background: $p-point-soft;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
  }

  &__speaker-display {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__speaker-avatar {
    flex-shrink: 0;
  }

  &__speaker-meta {
    flex: 1;
    min-width: 0;
  }

  &__speaker-name {
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
    margin-bottom: 2px;
  }

  &__speaker-name-en {
    font-size: 11px;
    color: $p-ink-mid;
  }

  /* ======================
     텍스트 쌍
     ====================== */
  &__text-pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__text-item {
    position: relative;

    &--source textarea.p-input {
      border-left: 3px solid $p-info;
    }

    &--target textarea.p-input {
      border-left: 3px solid $p-ok;
    }
  }

  &__char-count {
    position: absolute;
    bottom: 6px;
    right: 10px;
    font-size: 11px;
    color: $p-ink-mid;
    background: rgba(255, 255, 255, 0.9);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: $p-font-mono;
    pointer-events: none;

    &--inline {
      position: static;
      display: inline-block;
      margin-top: 4px;
      background: transparent;
      padding: 0;
    }
  }

  /* ======================
     파일 업로드
     ====================== */
  &__upload {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__upload-area {
    position: relative;
  }

  &__file-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__upload-label {
    display: inline-block;
    cursor: pointer;
    transition: all $p-dur $p-ease;

    &--audio {
      display: block;
      width: 100%;
    }
  }

  &__avatar {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid $p-point;
    background: $p-panel;

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &--sm {
      width: 40px;
      height: 40px;
      border-width: 1px;
    }
  }

  &__avatar-empty {
    width: 60px;
    height: 60px;
    border: 2px dashed $p-line-hard;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: $p-ink-mid;
    font-size: 10px;
    transition: all $p-dur $p-ease;
    background: $p-panel;

    > i {
      font-size: 20px;
      margin-bottom: 2px;
    }

    &:hover {
      border-color: $p-point-deep;
      color: $p-ink;
    }

    &--sm {
      width: 40px;
      height: 40px;
      border-width: 1px;

      > i { font-size: 16px; margin-bottom: 0; }
    }
  }

  /* ======================
     오디오 미리보기
     ====================== */
  &__audio-preview {
    width: 100%;
    height: 44px;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    background: $p-panel-alt;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color $p-dur $p-ease;

    &:hover {
      border-color: $p-line-hard;
    }
  }

  &__audio-controls {
    width: 100%;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 0 10px;
  }

  &__audio-info {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    color: $p-ink;
    font-weight: 500;
    font-size: 12px;

    > i {
      font-size: 14px;
      color: $p-point-deep;
      flex-shrink: 0;
    }
  }

  &__audio-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $p-ink-mid;
  }

  &__play-btn,
  &__change-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    border: 1px solid $p-line;
    background: $p-panel;
    color: $p-ink-mid;
    transition: background $p-dur $p-ease, color $p-dur $p-ease, border-color $p-dur $p-ease;
    cursor: pointer;
    flex-shrink: 0;

    > i { font-size: 14px; }

    &:hover {
      background: $p-ink;
      color: $p-point;
      border-color: $p-ink;
    }
  }

  &__audio-empty {
    width: 100%;
    height: 44px;
    border: 1px dashed $p-line-hard;
    border-radius: $p-radius-sm;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $p-panel-alt;
    transition: all $p-dur $p-ease;
    cursor: pointer;

    &:hover {
      border-color: $p-point-deep;
      background: $p-point-soft;
    }
  }

  &__audio-empty-inner {
    display: flex;
    align-items: center;
    gap: 8px;
    color: $p-ink-mid;
    font-size: 12px;
    font-weight: 600;

    > i {
      font-size: 14px;
      color: $p-point-deep;
    }
  }

  /* ======================
     업로드 로딩
     ====================== */
  &__upload-loading {
    width: 60px;
    height: 60px;
    border: 2px solid $p-point;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: $p-point-soft;
    position: relative;

    &--audio {
      width: 100%;
      height: 44px;
      border-radius: $p-radius-sm;
    }
  }

  &__progress-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    position: relative;
  }

  &__progress-circle {
    color: $p-point-deep;

    > i {
      font-size: 14px;
      animation: pg-pip-spin 1s linear infinite;
    }
  }

  &__progress-bar {
    width: 40px;
    height: 4px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    overflow: hidden;
    margin: 4px 0 2px;
  }

  &__progress-fill {
    height: 100%;
    background: $p-point-deep;
    border-radius: 2px;
    transition: width $p-dur $p-ease;
    min-width: 2px;
  }

  &__progress-text {
    font-size: 9px;
    font-weight: 700;
    color: $p-ink;
    font-family: $p-font-mono;
    line-height: 1;
  }

  /* ======================
     시간 설정
     ====================== */
  &__time-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__time-item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-sm;
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__time-icon {
    width: 36px;
    height: 36px;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    flex-shrink: 0;

    > i {
      font-size: 18px;
    }

    &--thinking {
      background: $p-ink;
      color: $p-point;
    }

    &--speaking {
      background: $p-point;
      color: $p-ink;
    }
  }

  &__time-content {
    flex: 1;
    min-width: 0;
  }

  /* ======================
     액션
     ====================== */
  &__actions {
    display: flex;
    justify-content: center;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed $p-line;
  }
}

@keyframes pg-pip-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
